import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { AlertTriangle, Boxes, Camera, CheckCircle2, Package, PackageOpen, PackagePlus, Printer, QrCode, RotateCcw, ScanLine, Send, Sparkles, Trash2, Truck, UserCog, X } from "lucide-react";
import { toast } from "sonner";
import { API, api, errMsg, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import PinConfirm from "../components/PinConfirm";
import { AssetUsageList } from "../components/aset/AssetUsageList";

const STATUS_STYLE = {
  gudang: { label: "Di Gudang", cls: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
  di_kt: { label: "Di Kepala Teknisi", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  di_teknisi: { label: "Dibawa Teknisi", cls: "bg-sky-500/10 text-sky-400 border-sky-500/20" },
  dikirim: { label: "Dikirim", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  terpasang: { label: "Terpasang", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
};

const MODE_TITLE = {
  inbound: { label: "Scan Alat Masuk (ke Gudang)", hint: "Scan serial number/barcode pabrik pada alat" },
  assign: { label: "Scan Kirim ke Kepala Teknisi", hint: "Scan barcode alat yang keluar dari gudang" },
  take: { label: "Scan Ambil Alat dari Kepala Teknisi", hint: "Scan barcode alat — stok berpindah ke akun Anda" },
  retur: { label: "Scan Retur ke Gudang", hint: "Scan barcode alat yang dikembalikan ke gudang (rusak / salah kirim)" },
  install: { label: "Scan Laporan Terpasang", hint: "Scan barcode alat yang sudah dipasang di pelanggan" },
};

function QrScanner({ onResult, onClose, testid }) {
  const [err, setErr] = useState("");
  useEffect(() => {
    const scanner = new Html5Qrcode("qr-reader");
    let stopped = false;
    const safeStop = () => {
      if (stopped) return;
      stopped = true;
      try {
        const p = scanner.stop();
        if (p && typeof p.catch === "function") p.catch(() => {}).finally(() => { try { scanner.clear(); } catch { /* noop */ } });
        else { try { scanner.clear(); } catch { /* noop */ } }
      } catch { try { scanner.clear(); } catch { /* noop */ } }
    };
    scanner.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: { width: 220, height: 220 } },
      (text) => { safeStop(); onResult(text.trim()); },
      () => {}
    ).catch(() => setErr("Kamera tidak tersedia / izin ditolak. Gunakan input manual."));
    return safeStop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="space-y-2" data-testid={testid}>
      <div id="qr-reader" className="rounded-xl overflow-hidden border border-border bg-black/40" />
      {err && <p className="text-xs text-amber-400" data-testid={`${testid}-error`}>{err}</p>}
      <Button variant="ghost" size="sm" className="w-full" onClick={onClose} data-testid={`${testid}-close`}><X className="w-4 h-4 mr-1" /> Tutup Kamera</Button>
    </div>
  );
}

export default function LacakAset() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const [units, setUnits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [cableLogs, setCableLogs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [stockMe, setStockMe] = useState(null);
  const [filter, setFilter] = useState("semua");
  const [busy, setBusy] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", category: "", qty: 1, serial: "" });
  const [aiBusy, setAiBusy] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [scanMode, setScanMode] = useState(null); // "inbound" | "assign" | "take" | "install"
  const [manualCode, setManualCode] = useState("");
  const [scanned, setScanned] = useState(null);
  const [inboundNew, setInboundNew] = useState(null);
  const [inboundForm, setInboundForm] = useState({ name: "", category: "" });
  const [assignForm, setAssignForm] = useState({ kt_email: "", destination: "" });
  const [installForm, setInstallForm] = useState({ customer: "", location: "", fo_cable_m: "", photo: null });
  const [returReason, setReturReason] = useState("");
  const [delTarget, setDelTarget] = useState(null);

  const load = () => {
    api.get("/asset-units").then((r) => setUnits(r.data)).catch((e) => toast.error(errMsg(e)));
    if (isAdmin) {
      api.get("/asset-units/summary").then((r) => setSummary(r.data)).catch(() => {});
      api.get("/fo-cable-report").then((r) => setCableLogs(r.data)).catch(() => {});
      api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    } else {
      api.get("/asset-units/stock/me").then((r) => setStockMe(r.data)).catch(() => {});
    }
  };
  useEffect(() => { load(); }, [isAdmin]);

  const ktList = users.filter((u) => u.role === "teknisi_kepala");
  const filtered = units.filter((u) => filter === "semua" || u.status === filter);
  const overdueCodes = new Set((summary?.overdue || []).map((o) => o.code));
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightCode = searchParams.get("unit") || "";

  useEffect(() => {
    if (!highlightCode || !units.length) return;
    setFilter("semua");
    const t = setTimeout(() => {
      document.querySelector(`[data-testid="unit-row-${highlightCode}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 500);
    const t2 = setTimeout(() => setSearchParams({}, { replace: true }), 10000);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [highlightCode, units]);

  const aiReadPhoto = async (file) => {
    if (!file) return;
    setAiBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/ai-read", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAddForm((f) => ({ ...f, name: data.name || f.name, category: data.category || f.category }));
      toast.success(`AI mengenali: ${data.name || "aset"}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setAiBusy(false); }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/asset-units", { ...addForm, qty: Number(addForm.qty) || 1 });
      toast.success(`${data.created.length} unit terdaftar: ${data.created[0].code}${data.created.length > 1 ? ` - ${data.created[data.created.length - 1].code}` : ""}`);
      setAddOpen(false); setAddForm({ name: "", category: "", qty: 1, serial: "" }); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const openPrint = async () => {
    try {
      const { data } = await api.get("/asset-units/print");
      if (!data.length) return toast.error("Belum ada unit terdaftar");
      setPrintData(data);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const closeScan = () => { setScanMode(null); setScanned(null); setInboundNew(null); setManualCode(""); };

  const lookup = async (code) => {
    const c = (code || "").trim().toUpperCase();
    if (!c) return;
    try {
      const { data } = await api.get(`/asset-units/${encodeURIComponent(c)}`);
      setScanned(data);
      setInboundNew(null);
      setAssignForm({ kt_email: "", destination: data.destination || "" });
      setInstallForm({ customer: data.customer || "", location: data.install_location || "", fo_cable_m: "", photo: null });
    } catch (err) {
      if (scanMode === "inbound" && err?.response?.status === 404) {
        setScanned(null); setInboundNew(c); setInboundForm({ name: "", category: "" });
      } else {
        toast.error(errMsg(err));
      }
    }
  };

  const submitInbound = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/asset-units", { name: inboundForm.name, category: inboundForm.category, serial: inboundNew, qty: 1 });
      toast.success(`Alat masuk gudang: ${data.created[0].code} • SN ${inboundNew}`);
      closeScan(); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const submitAssign = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const kt = ktList.find((u) => u.email === assignForm.kt_email);
      await api.post(`/asset-units/${scanned.code}/assign-kt`, { kt_email: assignForm.kt_email, destination: assignForm.destination });
      toast.success(`${scanned.code} keluar gudang → KT ${kt?.name || assignForm.kt_email}`);
      closeScan(); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const submitTake = async () => {
    setBusy(true);
    try {
      await api.post(`/asset-units/${scanned.code}/take`);
      toast.success(`${scanned.code} berpindah ke akun Anda — stok Kepala Teknisi berkurang`);
      closeScan(); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const submitRetur = async () => {
    setBusy(true);
    try {
      await api.post(`/asset-units/${scanned.code}/return`, { reason: returReason });
      toast.success(`${scanned.code} diretur ke gudang — ${scanned.holder_name || scanned.sent_to_name || "pemegang"} diberi notifikasi`);
      closeScan(); setReturReason(""); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const submitInstall = async (e) => {
    e.preventDefault();
    if (!installForm.photo) return toast.error("Foto hasil pekerjaan wajib dilampirkan");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("customer", installForm.customer);
      fd.append("location", installForm.location);
      fd.append("fo_cable_m", Number(installForm.fo_cable_m) || 0);
      fd.append("photo", installForm.photo);
      await api.post(`/asset-units/${scanned.code}/install`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${scanned.code} dilaporkan TERPASANG`);
      closeScan(); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const del = async () => {
    try { await api.delete(`/asset-units/${delTarget.id}`); toast.success(`Unit ${delTarget.code} dihapus`); setDelTarget(null); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const totalCable = cableLogs.reduce((s, l) => s + (l.meters || 0), 0);
  const modeMeta = MODE_TITLE[scanMode] || MODE_TITLE.install;

  return (
    <div className="space-y-6" data-testid="lacak-aset-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><ScanLine className="w-7 h-7 text-[#F04E23]" /> Lacak Aset</h1>
          <p className="text-sm text-muted-foreground mt-1">Alur stok: alat masuk gudang (scan serial pabrik) → admin kirim ke Kepala Teknisi → teknisi scan ambil → scan konfirmasi terpasang.</p>
        </div>
      </div>
      <AssetUsageList />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div />
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="btn-add-unit"><QrCode className="w-4 h-4 mr-1.5" /> Daftarkan Unit</Button>
              <Button variant="outline" onClick={openPrint} data-testid="btn-print-qr"><Printer className="w-4 h-4 mr-1.5" /> Cetak QR</Button>
              <Button variant="outline" onClick={() => { setScanMode("inbound"); setScanned(null); setInboundNew(null); }} data-testid="btn-scan-inbound"><PackagePlus className="w-4 h-4 mr-1.5" /> Scan Alat Masuk</Button>
              <Button onClick={() => { setScanMode("assign"); setScanned(null); }} data-testid="btn-scan-assign"><Truck className="w-4 h-4 mr-1.5" /> Scan Kirim ke KT</Button>
              <Button variant="outline" onClick={() => { setScanMode("retur"); setScanned(null); setReturReason(""); }} data-testid="btn-scan-retur"><RotateCcw className="w-4 h-4 mr-1.5" /> Scan Retur</Button>
            </>
          )}
          {!isAdmin && (
            <Button variant="outline" onClick={() => { setScanMode("take"); setScanned(null); }} data-testid="btn-scan-take"><PackageOpen className="w-4 h-4 mr-1.5" /> Scan Ambil Alat</Button>
          )}
          <Button onClick={() => { setScanMode("install"); setScanned(null); }} className={isAdmin ? "" : "flex-1"} data-testid="btn-scan-install"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Scan Terpasang</Button>
        </div>
      </div>

      {isAdmin && summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="asset-summary">
          <Card className="bg-card border-border"><CardContent className="p-4 flex items-center gap-3"><Boxes className="w-8 h-8 text-slate-400" /><div><div className="text-2xl font-bold font-mono" data-testid="count-gudang">{summary.gudang}</div><div className="text-xs text-muted-foreground">Di Gudang</div></div></CardContent></Card>
          <Card className="bg-card border-border"><CardContent className="p-4 flex items-center gap-3"><UserCog className="w-8 h-8 text-amber-400" /><div><div className="text-2xl font-bold font-mono" data-testid="count-di-kt">{summary.di_kt ?? 0}</div><div className="text-xs text-muted-foreground">Di Kepala Teknisi</div></div></CardContent></Card>
          <Card className="bg-card border-border"><CardContent className="p-4 flex items-center gap-3"><PackageOpen className="w-8 h-8 text-sky-400" /><div><div className="text-2xl font-bold font-mono" data-testid="count-di-teknisi">{summary.di_teknisi ?? 0}</div><div className="text-xs text-muted-foreground">Dibawa Teknisi</div></div></CardContent></Card>
          <Card className="bg-card border-border"><CardContent className="p-4 flex items-center gap-3"><CheckCircle2 className="w-8 h-8 text-emerald-400" /><div><div className="text-2xl font-bold font-mono" data-testid="count-terpasang">{summary.terpasang}</div><div className="text-xs text-muted-foreground">Terpasang</div></div></CardContent></Card>
          <Card className={`border ${summary.overdue.length ? "bg-rose-500/10 border-rose-500/30" : "bg-card border-border"}`}><CardContent className="p-4 flex items-center gap-3"><AlertTriangle className={`w-8 h-8 ${summary.overdue.length ? "text-rose-400" : "text-slate-500"}`} /><div><div className={`text-2xl font-bold font-mono ${summary.overdue.length ? "text-rose-400" : ""}`} data-testid="count-overdue">{summary.overdue.length}</div><div className="text-xs text-muted-foreground">Belum terpasang &gt; {summary.overdue_days} hari</div></div></CardContent></Card>
        </div>
      )}

      {isAdmin && (summary?.per_holder || []).length > 0 && (
        <Card className="bg-card border-border shadow-sm" data-testid="holder-stock-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><UserCog className="w-4 h-4 text-amber-400" /> Stok per Penanggung Jawab</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {summary.per_holder.map((h) => (
              <div key={h.email} className="flex items-center justify-between gap-3 text-xs rounded-lg border border-border bg-accent/30 px-3 py-2" data-testid={`holder-stock-${h.email}`}>
                <span className="min-w-0 truncate"><b>{h.name || h.email}</b> <span className="text-muted-foreground">({h.email})</span></span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${h.sebagai === "Kepala Teknisi" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : "bg-sky-500/10 text-sky-400 border-sky-500/20"}`}>{h.sebagai}</span>
                  <b className="font-mono">{h.count} unit</b>
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {!isAdmin && stockMe && (
        <Card className="bg-card border-border shadow-sm" data-testid="my-stock-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Package className="w-4 h-4 text-emerald-400" /> Stok Alat di Akun Saya ({stockMe.count})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {!stockMe.count && <p className="text-xs text-muted-foreground" data-testid="my-stock-empty">Tidak ada alat di akun Anda. Gunakan "Scan Ambil Alat" untuk mengambil dari Kepala Teknisi.</p>}
            <div className="flex flex-wrap gap-2">
              {stockMe.units.map((u) => (
                <span key={u.id} className="px-2.5 py-1.5 rounded-lg border border-border bg-accent/40 text-xs font-mono" data-testid={`my-stock-${u.code}`}>
                  <b className="text-[#F04E23]">{u.code}</b> {u.name}{u.serial ? <span className="text-muted-foreground"> • SN {u.serial}</span> : ""}
                </span>
              ))}
            </div>
            {(stockMe.downlines || []).length > 0 && (
              <div className="pt-2 border-t border-border space-y-1" data-testid="my-downline-stock">
                <p className="text-[11px] text-muted-foreground font-semibold">Sedang dibawa teknisi Anda:</p>
                {stockMe.downlines.map((d) => (
                  <div key={d.email} className="flex justify-between text-xs" data-testid={`downline-stock-${d.email}`}><span>{d.name || d.email}</span><b className="font-mono text-sky-400">{d.count} unit</b></div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {isAdmin && (summary?.overdue || []).length > 0 && (
        <Card className="bg-rose-500/5 border-rose-500/30" data-testid="overdue-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2 text-rose-400"><AlertTriangle className="w-4 h-4" /> Indikasi Selisih — Keluar gudang tapi belum dilaporkan terpasang &gt; {summary.overdue_days} hari</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {summary.overdue.map((o) => (
              <div key={o.code} className="flex flex-wrap items-center gap-2 rounded-xl border border-rose-500/20 bg-background/40 px-4 py-2.5" data-testid={`overdue-${o.code}`}>
                <span className="font-mono font-bold text-rose-400">{o.code}</span>
                <span className="text-sm font-medium">{o.name}</span>
                <span className="text-xs text-muted-foreground">di <span className="text-foreground font-medium">{o.holder_name || o.sent_to_name || o.sent_to_email}</span>{o.destination ? ` • ${o.destination}` : ""}</span>
                <span className="ml-auto px-2 py-0.5 rounded-full text-[11px] font-mono bg-rose-500/15 text-rose-400 border border-rose-500/30">{o.hari_berjalan} hari berjalan</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="units-card">
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base font-heading flex items-center gap-2"><Package className="w-4 h-4 text-sky-400" /> Daftar Unit Aset</CardTitle>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="select-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semua">Semua Status</SelectItem>
              {Object.entries(STATUS_STYLE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardHeader>
        <Table className="min-w-[900px]" data-testid="units-table">
          <TableHeader>
            <TableRow>
              <TableHead>Kode / Serial</TableHead><TableHead>Nama Aset</TableHead><TableHead>Status</TableHead>
              <TableHead>Penanggung Jawab</TableHead><TableHead>Pemasangan</TableHead><TableHead>Kabel FO</TableHead><TableHead>Foto</TableHead>
              {isAdmin && <TableHead className="text-right">Aksi</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!filtered.length && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground" data-testid="units-empty">Belum ada unit. Admin scan serial alat masuk atau daftarkan unit manual.</TableCell></TableRow>}
            {filtered.map((u) => {
              const st = STATUS_STYLE[u.status] || STATUS_STYLE.gudang;
              const overdue = overdueCodes.has(u.code);
              return (
                <TableRow key={u.id} className={`${overdue ? "bg-rose-500/5" : ""} ${highlightCode === u.code ? "ring-2 ring-inset ring-[#F04E23] bg-[#F04E23]/10" : ""}`} data-testid={`unit-row-${u.code}`}>
                  <TableCell>
                    <span className="px-2 py-0.5 rounded text-xs font-mono font-bold bg-[#F04E23]/10 text-[#F04E23] border border-[#F04E23]/20">{u.code}</span>
                    {u.serial && <div className="text-[10px] font-mono text-muted-foreground mt-0.5" data-testid={`unit-serial-${u.code}`}>SN {u.serial}</div>}
                  </TableCell>
                  <TableCell><div className="text-sm font-medium">{u.name}</div>{u.category && <div className="text-[10px] text-muted-foreground">{u.category}</div>}</TableCell>
                  <TableCell>
                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-mono border ${st.cls}`} data-testid={`unit-status-${u.code}`}>{st.label}</span>
                    {overdue && <span className="ml-1 px-2 py-0.5 rounded-full text-[11px] font-mono bg-rose-500/15 text-rose-400 border border-rose-500/30" data-testid={`unit-overdue-${u.code}`}>Selisih?</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {u.holder_name ? (
                      <><div className="font-medium" data-testid={`unit-holder-${u.code}`}>{u.holder_name}</div><div className="text-muted-foreground">{fmtDateTime(u.holder_since)}</div></>
                    ) : u.sent_to_name ? (
                      <><div className="font-medium">{u.sent_to_name}</div><div className="text-muted-foreground">{u.destination || "-"} • {fmtDateTime(u.sent_at)}</div></>
                    ) : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {u.status === "terpasang" ? <><div className="font-medium">{u.customer || "-"}</div><div className="text-muted-foreground">{u.install_location || "-"} • oleh {u.installed_by_name || u.installed_by}</div><div className="text-muted-foreground">{fmtDateTime(u.installed_at)}</div></> : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="font-mono text-xs" data-testid={`unit-cable-${u.code}`}>{u.fo_cable_m ? `${u.fo_cable_m} m` : "-"}</TableCell>
                  <TableCell>
                    {u.photo_path ? (
                      <a href={`${API}/asset-units/files/${u.photo_path}`} target="_blank" rel="noreferrer" data-testid={`unit-photo-${u.code}`}>
                        <img src={`${API}/asset-units/files/${u.photo_path}`} alt="foto pemasangan" className="w-12 h-12 rounded-lg object-cover border border-border hover:opacity-80 transition-opacity" />
                      </a>
                    ) : <span className="text-xs text-muted-foreground">-</span>}
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right"><Button size="icon" variant="ghost" className="text-rose-400" onClick={() => setDelTarget(u)} data-testid={`btn-del-unit-${u.code}`}><Trash2 className="w-4 h-4" /></Button></TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {isAdmin && (
        <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="cable-report-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><ScanLine className="w-4 h-4 text-[#F04E23]" /> Laporan Pemakaian Kabel FO — total <span className="font-mono text-foreground" data-testid="cable-total">{totalCable.toLocaleString("id-ID")} m</span></CardTitle></CardHeader>
          <Table className="min-w-[720px]" data-testid="cable-table">
            <TableHeader>
              <TableRow><TableHead>Waktu</TableHead><TableHead>Teknisi</TableHead><TableHead>Pelanggan / Lokasi</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Pemakaian</TableHead><TableHead>Foto</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {!cableLogs.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="cable-empty">Belum ada laporan pemakaian kabel</TableCell></TableRow>}
              {cableLogs.map((l) => (
                <TableRow key={l.id} data-testid={`cable-row-${l.id}`}>
                  <TableCell className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">{fmtDateTime(l.created_at)}</TableCell>
                  <TableCell className="text-sm font-medium">{l.teknisi}</TableCell>
                  <TableCell className="text-xs"><div className="font-medium">{l.customer || "-"}</div><div className="text-muted-foreground">{l.location || "-"}</div></TableCell>
                  <TableCell className="font-mono text-xs">{l.unit_code}<div className="text-muted-foreground">{l.unit_name}</div></TableCell>
                  <TableCell className="text-right font-mono font-bold text-sky-400" data-testid={`cable-meters-${l.id}`}>{l.meters} m</TableCell>
                  <TableCell>
                    {l.photo_path ? (
                      <a href={`${API}/asset-units/files/${l.photo_path}`} target="_blank" rel="noreferrer" data-testid={`cable-photo-${l.id}`}>
                        <img src={`${API}/asset-units/files/${l.photo_path}`} alt="foto" className="w-10 h-10 rounded-lg object-cover border border-border hover:opacity-80 transition-opacity" />
                      </a>
                    ) : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Dialog: Daftarkan Unit */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="dialog-add-unit">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-[#F04E23]" /> Daftarkan Unit Aset</DialogTitle></DialogHeader>
          <form onSubmit={submitAdd} className="space-y-3">
            <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#F04E23]" /> Foto Aset — AI isi otomatis (opsional)</Label>
              <Input type="file" accept="image/*" capture="environment" disabled={aiBusy} onChange={(e) => aiReadPhoto(e.target.files?.[0])} data-testid="input-unit-ai-photo" />
              {aiBusy && <p className="text-[11px] text-[#F04E23] animate-pulse" data-testid="ai-reading">AI sedang membaca foto...</p>}
            </div>
            <div className="space-y-1.5"><Label>Ambil dari Daftar Aset (opsional)</Label>
              <Select value="-" onValueChange={(v) => { const a = assets.find((x) => x.id === v); if (a) setAddForm({ ...addForm, name: a.name, category: a.category || "" }); }}>
                <SelectTrigger data-testid="select-from-assets"><SelectValue placeholder="Pilih aset untuk mengisi otomatis" /></SelectTrigger>
                <SelectContent>
                  {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.category ? ` — ${a.category}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Nama Aset</Label><Input required value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="cth: ONT ZTE F609" data-testid="input-unit-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Kategori</Label><Input value={addForm.category} onChange={(e) => setAddForm({ ...addForm, category: e.target.value })} placeholder="cth: Perangkat" data-testid="input-unit-category" /></div>
              <div className="space-y-1.5"><Label>Jumlah Unit</Label><Input type="number" min="1" max="100" required value={addForm.qty} onChange={(e) => setAddForm({ ...addForm, qty: e.target.value })} disabled={!!addForm.serial.trim()} data-testid="input-unit-qty" /></div>
            </div>
            <div className="space-y-1.5"><Label>Serial Number Pabrik (opsional)</Label><Input value={addForm.serial} onChange={(e) => setAddForm({ ...addForm, serial: e.target.value })} placeholder="scan/ketik SN alat — 1 SN = 1 unit" className="font-mono" data-testid="input-unit-serial" /></div>
            <p className="text-[11px] text-muted-foreground">Setiap unit mendapat kode unik berformat FSN-KATEGORI-nomor beserta QR Code. Jika serial diisi, unit bisa discan langsung dari barcode pabriknya.</p>
            <Button type="submit" className="w-full" disabled={busy} data-testid="btn-save-unit">{busy ? "Memproses..." : "Daftarkan & Buat QR"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Cetak QR */}
      <Dialog open={!!printData} onOpenChange={() => setPrintData(null)}>
        <DialogContent className="max-w-3xl" data-testid="dialog-print-qr">
          <DialogHeader><DialogTitle>Cetak QR Aset ({printData?.length || 0} unit)</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <div id="qr-print-area" className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {(printData || []).map((p) => (
                <div key={p.code} className="rounded-xl border border-border p-3 text-center break-inside-avoid" data-testid={`qr-item-${p.code}`}>
                  <img src={p.qr} alt={`QR ${p.code}`} className="w-full aspect-square object-contain" />
                  <div className="font-mono font-bold text-xs mt-1">{p.code}</div>
                  <div className="text-[10px] text-muted-foreground leading-tight">{p.name}</div>
                </div>
              ))}
            </div>
          </div>
          <Button className="w-full" onClick={() => window.print()} data-testid="btn-do-print"><Printer className="w-4 h-4 mr-1.5" /> Print</Button>
        </DialogContent>
      </Dialog>

      {/* Dialog: Scan (Masuk / Kirim ke KT / Ambil / Terpasang) */}
      <Dialog open={!!scanMode} onOpenChange={closeScan}>
        <DialogContent data-testid="dialog-scan">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {scanMode === "inbound" && <><PackagePlus className="w-5 h-5 text-[#F04E23]" /> {modeMeta.label}</>}
              {scanMode === "assign" && <><Truck className="w-5 h-5 text-amber-400" /> {modeMeta.label}</>}
              {scanMode === "take" && <><PackageOpen className="w-5 h-5 text-sky-400" /> {modeMeta.label}</>}
              {scanMode === "retur" && <><RotateCcw className="w-5 h-5 text-rose-400" /> {modeMeta.label}</>}
              {scanMode === "install" && <><CheckCircle2 className="w-5 h-5 text-emerald-400" /> {modeMeta.label}</>}
            </DialogTitle>
          </DialogHeader>
          {!scanned && !inboundNew && (
            <div className="space-y-3">
              <p className="text-[11px] text-muted-foreground">{modeMeta.hint}</p>
              <QrScanner onResult={lookup} onClose={closeScan} testid={`scanner-${scanMode}`} />
              <div className="flex gap-2">
                <Input value={manualCode} onChange={(e) => setManualCode(e.target.value)} placeholder="Atau ketik kode unit / serial pabrik" className="font-mono" data-testid="input-manual-code" />
                <Button onClick={() => lookup(manualCode)} data-testid="btn-lookup-code"><Camera className="w-4 h-4 mr-1" /> Cari</Button>
              </div>
            </div>
          )}

          {inboundNew && !scanned && (
            <form onSubmit={submitInbound} className="space-y-3">
              <div className="rounded-xl border border-[#F04E23]/30 bg-[#F04E23]/10 px-4 py-3" data-testid="inbound-new-info">
                <div className="text-xs text-muted-foreground">Serial baru belum terdaftar — lengkapi data alat masuk:</div>
                <div className="font-mono font-bold text-[#F04E23] mt-0.5" data-testid="inbound-serial">SN {inboundNew}</div>
              </div>
              <div className="space-y-1.5"><Label>Nama Alat</Label><Input required value={inboundForm.name} onChange={(e) => setInboundForm({ ...inboundForm, name: e.target.value })} placeholder="cth: ONT ZTE F609" data-testid="input-inbound-name" /></div>
              <div className="space-y-1.5"><Label>Kategori</Label><Input value={inboundForm.category} onChange={(e) => setInboundForm({ ...inboundForm, category: e.target.value })} placeholder="cth: Perangkat" data-testid="input-inbound-category" /></div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="btn-confirm-inbound"><PackagePlus className="w-4 h-4 mr-1.5" /> {busy ? "Memproses..." : "Daftarkan ke Gudang"}</Button>
              <Button type="button" variant="ghost" className="w-full" onClick={() => setInboundNew(null)} data-testid="btn-inbound-rescan">Scan Ulang</Button>
            </form>
          )}

          {scanned && (
            <div className="space-y-3">
              <div className="rounded-xl border border-border bg-accent/30 px-4 py-3 flex items-center gap-3" data-testid="scanned-info">
                <span className="font-mono font-bold text-[#F04E23]">{scanned.code}</span>
                <div className="min-w-0">
                  <div className="font-medium text-sm">{scanned.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {(STATUS_STYLE[scanned.status] || STATUS_STYLE.gudang).label}
                    {scanned.serial ? ` • SN ${scanned.serial}` : ""}
                    {scanned.holder_name ? ` • di ${scanned.holder_name}` : ""}
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setScanned(null)} data-testid="btn-rescan">Scan Ulang</Button>
              </div>

              {scanMode === "inbound" && (
                <p className="text-xs text-amber-400" data-testid="inbound-exists">Serial ini sudah terdaftar (status: {(STATUS_STYLE[scanned.status] || STATUS_STYLE.gudang).label}). Tidak bisa didaftarkan ulang.</p>
              )}

              {scanMode === "assign" && isAdmin && (
                scanned.status !== "gudang" ? (
                  <p className="text-xs text-amber-400" data-testid="assign-not-gudang">Unit tidak berada di gudang — hanya unit Di Gudang yang bisa dikirim ke Kepala Teknisi.</p>
                ) : (
                  <form onSubmit={submitAssign} className="space-y-3">
                    <div className="space-y-1.5"><Label>Kepala Teknisi Penerima</Label>
                      <Select value={assignForm.kt_email} onValueChange={(v) => setAssignForm({ ...assignForm, kt_email: v })}>
                        <SelectTrigger data-testid="select-kt"><SelectValue placeholder="Pilih Kepala Teknisi" /></SelectTrigger>
                        <SelectContent>
                          {ktList.map((u) => <SelectItem key={u.email} value={u.email} data-testid={`kt-opt-${u.email}`}>{u.name} — {u.email}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {!ktList.length && <p className="text-[11px] text-amber-400">Belum ada pengguna ber-role Teknisi Kepala — atur di menu Agen → Edit Pengguna.</p>}
                    </div>
                    <div className="space-y-1.5"><Label>Tujuan / Lokasi</Label><Input value={assignForm.destination} onChange={(e) => setAssignForm({ ...assignForm, destination: e.target.value })} placeholder="cth: Basecamp KT JESSY.NET" data-testid="input-destination" /></div>
                    <Button type="submit" className="w-full" disabled={busy || !assignForm.kt_email} data-testid="btn-confirm-assign"><Send className="w-4 h-4 mr-1.5" /> {busy ? "Memproses..." : "Kirim ke Kepala Teknisi"}</Button>
                  </form>
                )
              )}

              {scanMode === "take" && (
                scanned.status !== "di_kt" ? (
                  <p className="text-xs text-amber-400" data-testid="take-not-kt">Unit tidak sedang berada di akun Kepala Teknisi (status: {(STATUS_STYLE[scanned.status] || STATUS_STYLE.gudang).label}).</p>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground" data-testid="take-info">Alat ini berada di akun Kepala Teknisi <b className="text-foreground">{scanned.holder_name}</b>. Setelah Anda konfirmasi, stok berpindah ke akun Anda dan stok Kepala Teknisi berkurang.</p>
                    <Button className="w-full" onClick={submitTake} disabled={busy} data-testid="btn-confirm-take"><PackageOpen className="w-4 h-4 mr-1.5" /> {busy ? "Memproses..." : "Konfirmasi Ambil Alat"}</Button>
                  </div>
                )
              )}

              {scanMode === "retur" && isAdmin && (
                ["gudang", "terpasang"].includes(scanned.status) ? (
                  <p className="text-xs text-amber-400" data-testid="retur-invalid">{scanned.status === "gudang" ? "Unit sudah berada di gudang." : "Unit sudah terpasang — tidak bisa diretur."}</p>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground" data-testid="retur-info">Alat ini sedang di akun <b className="text-foreground">{scanned.holder_name || scanned.sent_to_name || "-"}</b>. Setelah diretur, status kembali <b className="text-foreground">Di Gudang</b>, stok pemegang berkurang, dan pemegang mendapat notifikasi chat.</p>
                    <div className="space-y-1.5"><Label>Alasan Retur (opsional)</Label><Input value={returReason} onChange={(e) => setReturReason(e.target.value)} placeholder="cth: alat rusak / salah kirim" data-testid="input-retur-reason" /></div>
                    <Button className="w-full bg-rose-600 hover:bg-rose-500 text-white" onClick={submitRetur} disabled={busy} data-testid="btn-confirm-retur"><RotateCcw className="w-4 h-4 mr-1.5" /> {busy ? "Memproses..." : "Konfirmasi Retur ke Gudang"}</Button>
                  </div>
                )
              )}

              {scanMode === "install" && (
                <form onSubmit={submitInstall} className="space-y-3">
                  {scanned.status === "terpasang" && <p className="text-xs text-amber-400" data-testid="install-already">Unit ini sudah dilaporkan terpasang.</p>}
                  {scanned.status === "gudang" && <p className="text-xs text-amber-400" data-testid="install-gudang">Unit masih di gudang — teknisi wajib scan ambil alat terlebih dahulu.</p>}
                  <div className="space-y-1.5"><Label>Nama Pelanggan</Label><Input required value={installForm.customer} onChange={(e) => setInstallForm({ ...installForm, customer: e.target.value })} placeholder="cth: Budi Santoso" data-testid="input-customer" /></div>
                  <div className="space-y-1.5"><Label>Lokasi Pemasangan</Label><Input required value={installForm.location} onChange={(e) => setInstallForm({ ...installForm, location: e.target.value })} placeholder="cth: Jl. Merdeka No. 12" data-testid="input-location" /></div>
                  <div className="space-y-1.5"><Label>Pemakaian Kabel FO (meter)</Label><Input type="number" min="0" step="0.5" value={installForm.fo_cable_m} onChange={(e) => setInstallForm({ ...installForm, fo_cable_m: e.target.value })} placeholder="cth: 75" data-testid="input-cable-m" /></div>
                  <div className="space-y-1.5"><Label>Foto Hasil Pekerjaan (wajib)</Label>
                    <Input type="file" accept="image/*" capture="environment" required onChange={(e) => setInstallForm({ ...installForm, photo: e.target.files?.[0] || null })} data-testid="input-install-photo" />
                    <p className="text-[11px] text-muted-foreground">Ambil foto perangkat terpasang di lokasi pelanggan sebagai bukti.</p>
                  </div>
                  <Button type="submit" className="w-full" disabled={busy || scanned.status === "terpasang" || scanned.status === "gudang"} data-testid="btn-confirm-install"><CheckCircle2 className="w-4 h-4 mr-1.5" /> {busy ? "Mengunggah..." : "Laporkan TERPASANG"}</Button>
                </form>
              )}
              {scanMode === "assign" && !isAdmin && <p className="text-xs text-muted-foreground">Hanya admin yang dapat mengirim alat dari gudang.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hapus unit — wajib PIN */}
      <PinConfirm open={!!delTarget} onOpenChange={() => setDelTarget(null)}
        title={`Hapus Unit ${delTarget?.code}?`}
        description={`Unit ${delTarget?.name} (${delTarget?.code}) akan dihapus dari pelacakan.`}
        confirmLabel="Ya, Hapus Unit" destructive onConfirm={del} testid="pin-del-unit" />
    </div>
  );
}
